import { useState } from 'react';
import { ConditionSingleValueDateProps } from 'ui/queryBuilder/types';
import { validateDate as hasDateError } from 'utils/validateDate';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { guessCurrentTimeZone } from 'utils/datetime';
import { isNotNull } from 'utils/tsHelpers';

import { getOnChangeValue } from '../utils/getOnChangeValue';
import { convertToParsedDate } from '../utils/convertToParsedDate';
import { getDatePickerDate, getOnDateChange, getIsRelativeDate } from '../utils';
import { useGetDisplayDate } from './useGetDisplayDate';

export const useConditionDateState = ({
  value,
  defaultTimeZone,
  onChange,
  isOpenByDefault = false,
}: Pick<
  ConditionSingleValueDateProps,
  'value' | 'defaultTimeZone' | 'onChange' | 'isOpenByDefault'
>) => {
  /** state */
  const [isEditing, setIsEditing] = useState<boolean>(isOpenByDefault);
  const [debouncedDate, date, setDate] = useDebouncedState<string | null>(
    getDatePickerDate(value),
    300,
  );
  const [hasSelectedDate, setHasSelectedDate] = useState<boolean>(false);
  const [timeZone, setTimeZone] = useState(defaultTimeZone ?? guessCurrentTimeZone());
  const [isRelativeDate, setIsRelativeDate] = useState<boolean>(getIsRelativeDate(date));
  const triggerDate = useGetDisplayDate(isEditing ? debouncedDate : value, timeZone);

  const defaultDate = isRelativeDate ? '' : convertToParsedDate();

  /** event fn */
  const onDateChange = getOnDateChange(setDate, setHasSelectedDate);
  const onRelativeDateToggle = (status: boolean) => {
    setDate(null); // reset date
    setIsRelativeDate(status);
  };
  const onBlurSave = (editingMode: boolean) => {
    if (
      !editingMode &&
      isNotNull(date) &&
      // Ensure we are not setting an unselected value on blur
      hasSelectedDate
    ) {
      const onChangeValue = getOnChangeValue(date, isRelativeDate);
      if (!hasDateError(onChangeValue)) onChange(onChangeValue);
    }
    setIsEditing(editingMode);
  };

  return {
    date,
    isEditing,
    timeZone,
    isRelativeDate,
    triggerDate: hasSelectedDate || isNotNull(value) ? triggerDate : null,
    defaultDate,
    onTimeZoneChange: setTimeZone,
    onBlurSave,
    onDateChange,
    onRelativeDateToggle,
  };
};
